import { addMinutes, subMinutes, toDate } from 'date-fns';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

interface TimePickerMobileProps {
  date: string | number | Date;
  isStartTime?: boolean;
  isFifteenMinutesResolution?: boolean;
  updateTime: (date: string | number | Date) => void;
}

export function TimePickerMobile({
  date: originalDate,
  isFifteenMinutesResolution,
  isStartTime,
  updateTime,
}: Readonly<TimePickerMobileProps>): ReactElement<TimePickerMobileProps> {
  const [t] = useTranslation();

  const date = toDate(originalDate);
  const increaseTime = (byQuarterHour: boolean) => () => {
    const updatedDate = addMinutes(date, byQuarterHour ? 15 : 60);
    date.setHours(updatedDate.getHours(), updatedDate.getMinutes());
    updateTime(date);
  };
  const decreaseTime = (byQuarterHour: boolean) => () => {
    const updatedDate = subMinutes(date, byQuarterHour ? 15 : 60);
    date.setHours(updatedDate.getHours(), updatedDate.getMinutes());
    updateTime(date);
  };

  const stepperStyle = 'appearance-none bg-transparent border-none px-2 py-4 m-0 active:bg-black/5 transition-colors';

  return (
    <div className="flex items-center">
      <div className="box-border flex items-center rounded-l-lg border border-solid border-green-200 bg-green-100">
        <button
          className={stepperStyle}
          title={t('FORECAST_ASSISTANT_TIME_ONE_HOUR_EARLIER')}
          onClick={decreaseTime(false)}
        >
          <div className="size-3 bg-green-200 [clip-path:polygon(60%_10%,_20%_50%,_60%_90%,_50%_100%,_0_50%,_50%_0,_60%_10%,_90%_0,_100%_10%,_60%_50%,_100%_90%,_90%_100%,_40%_50%,_90%_0)]" />
        </button>
        {isFifteenMinutesResolution && (
          <>
            <div className="h-4 w-px bg-green-200" />
            <button
              className={stepperStyle}
              title={t('FORECAST_ASSISTANT_TIME_FIFTEEN_MINUTES_EARLIER')}
              onClick={decreaseTime(true)}
            >
              <div className="my-0.5 size-2 bg-green-200 [clip-path:polygon(75%_10%,_65%_0,_15%_50%,_65%_100%,_75%_90%,_35%_50%)]" />
            </button>
          </>
        )}
      </div>
      <div className="relative box-content flex h-11 w-12 items-center justify-center border-0 border-y border-solid border-green-200 text-base leading-none">
        {date.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: isStartTime ? 'h23' : 'h24',
        })}
        <div className="absolute -top-2 bg-white px-1 text-xs text-green-200">
          {t(isStartTime ? 'SHARED_DATE_FROM_MOBILE' : 'SHARED_DATE_TO_MOBILE')}
        </div>
      </div>
      <div className="box-border flex items-center rounded-r-lg border border-solid border-green-200 bg-green-100">
        {isFifteenMinutesResolution && (
          <>
            <button
              className={stepperStyle}
              title={t('FORECAST_ASSISTANT_TIME_FIFTEEN_MINUTES_LATER')}
              onClick={increaseTime(true)}
            >
              <div className="my-0.5 size-2 bg-green-200 [clip-path:polygon(25%_10%,_35%_0,_85%_50%,_35%_100%,_25%_90%,_65%_50%)]" />
            </button>
            <div className="h-4 w-px bg-green-200" />
          </>
        )}
        <button
          className={stepperStyle}
          title={t('FORECAST_ASSISTANT_TIME_ONE_HOUR_LATER')}
          onClick={increaseTime(false)}
        >
          <div className="size-3 bg-green-200 [clip-path:polygon(40%_10%,_80%_50%,_40%_90%,_50%_100%,_100%_50%,_50%_0,_40%_10%,_10%_0,_0_10%,_40%_50%,_0_90%,_10%_100%,_60%_50%,_10%_0)]" />
        </button>
      </div>
    </div>
  );
}
